import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle, XCircle, Pause, Clock } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function Row({ children, testId }) {
  return (
    <li className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-5 py-3" data-testid={testId}>
      {children}
    </li>
  );
}

export default function WorkApprovals() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["work-approvals"],
    queryFn: async () => (await api.get("/work/approvals")).data,
    refetchInterval: 60000,
  });

  const pauses = data?.pauses || [];
  const longShifts = data?.long_shifts || [];
  if (pauses.length === 0 && longShifts.length === 0) return null;

  const act = async (fn, label) => {
    try {
      await fn();
      toast.success(label);
      qc.invalidateQueries({ queryKey: ["work-approvals"] });
      qc.invalidateQueries({ queryKey: ["work-assignments"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["entity"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/50 shadow-none" data-testid="work-approvals">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-amber-100">
        <Clock className="h-5 w-5 text-amber-600" weight="fill" />
        <h3 className="font-semibold text-amber-900">
          Work Approvals — {pauses.length} break{pauses.length === 1 ? "" : "s"}, {longShifts.length} long shift{longShifts.length === 1 ? "" : "s"}
        </h3>
      </div>

      <ul className="divide-y divide-amber-100">
        {pauses.map((p) => (
          <Row key={p.pause_id} testId={`pause-approval-${p.pause_id}`}>
            <span className="text-sm min-w-0">
              <Pause className="h-3.5 w-3.5 inline mr-1 text-amber-600" weight="fill" />
              <span className="font-mono text-xs text-slate-500">{p.session_code}</span>{" "}
              <span className="text-slate-900">{p.operator}</span> · {p.job} · {p.machine} —{" "}
              <span className="font-medium">{p.reason}</span> ({p.hours} h)
              {p.note && <span className="text-slate-500"> · {p.note}</span>}
            </span>
            <span className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" className="h-7 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                onClick={() => act(() => api.post(`/work/sessions/${p.session_id}/pauses/${p.pause_id}/approval`, { status: "Approved" }), "Break approved")}
                data-testid={`pause-approve-${p.pause_id}`}>
                <CheckCircle className="h-4 w-4 mr-1" weight="fill" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-red-700 border-red-200 hover:bg-red-50"
                onClick={() => act(() => api.post(`/work/sessions/${p.session_id}/pauses/${p.pause_id}/approval`, { status: "Rejected" }), "Break rejected")}
                data-testid={`pause-reject-${p.pause_id}`}>
                <XCircle className="h-4 w-4 mr-1" weight="fill" /> Reject
              </Button>
            </span>
          </Row>
        ))}

        {longShifts.map((s) => (
          <Row key={s.session_id} testId={`longshift-approval-${s.session_id}`}>
            <span className="text-sm min-w-0">
              <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200 mr-2">Over 12 h</Badge>
              <span className="font-mono text-xs text-slate-500">{s.session_code}</span>{" "}
              <span className="text-slate-900">{s.operator}</span> · {s.job} · {s.machine} —{" "}
              <span className="font-medium">{s.gross_hours} h straight</span> ({s.actual_hours} h payable)
            </span>
            <span className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" className="h-7 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                onClick={() => act(() => api.post(`/work/sessions/${s.session_id}/long-shift-approval`, { status: "Approved" }), "Long shift approved")}
                data-testid={`longshift-approve-${s.session_id}`}>
                <CheckCircle className="h-4 w-4 mr-1" weight="fill" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-red-700 border-red-200 hover:bg-red-50"
                onClick={() => act(() => api.post(`/work/sessions/${s.session_id}/long-shift-approval`, { status: "Rejected" }), "Long shift rejected")}
                data-testid={`longshift-reject-${s.session_id}`}>
                <XCircle className="h-4 w-4 mr-1" weight="fill" /> Reject
              </Button>
            </span>
          </Row>
        ))}
      </ul>
    </Card>
  );
}
